import { useCallback, useRef, useState } from 'react';
import * as Sentry from '@sentry/nextjs';

import PrePurchaseQuestionsComponent from 'features/forms/components/events/PrePurchaseQuestions';
import Button from 'components/Button';
import {
  useCreatePrePurchaseResponseMutation,
  useUpdatePrePurchaseResponseMutation
} from 'api/queries/events/pre-purchase-questions';
import { MdArrowBack } from 'react-icons/md';
import { error } from 'legacy/common/utils/toast';
import Loader from 'legacy/common/components/layout/Loader';

function PrePurchaseQuestions({
  eventId,
  prePurchaseFormId,
  onGoBack,
  onClaimTicket,
  isLoadingClaimShareToken
}) {
  const [canSubmit, setCanSubmit] = useState(false);
  const prePurchaseQuestionsRef = useRef<{
    submit: () => void;
    reset: () => void;
  }>(null);

  const {
    mutateAsync: createPrePurchaseResponse,
    isLoading: isCreatingPrePurchaseResponse
  } = useCreatePrePurchaseResponseMutation();
  const {
    mutateAsync: updatePrePurchaseResponse,
    isLoading: isUpdatingPrePurchaseResponse
  } = useUpdatePrePurchaseResponseMutation();

  const isLoading =
    isCreatingPrePurchaseResponse ||
    isUpdatingPrePurchaseResponse ||
    isLoadingClaimShareToken;

  const onComplete = useCallback(
    async ({
      data,
      isEditing
    }: {
      data: Record<string, any>;
      isEditing: boolean;
    }) => {
      try {
        const response = await (isEditing
          ? updatePrePurchaseResponse
          : createPrePurchaseResponse)({
          eventId,
          response: { response: data }
        });

        if (response.responderId) {
          onClaimTicket();
        }
      } catch (err) {
        prePurchaseQuestionsRef?.current?.reset();
        error(err.message || 'Error creating pre purchase response');

        Sentry.captureException(err, {
          tags: {
            component: 'CheckoutPrePurchaseQuestionsStep',
            action: 'onComplete'
          }
        });
      }
    },
    [eventId]
  );

  const onConfirmPrePurchaseQuestions = () => {
    prePurchaseQuestionsRef?.current?.submit();
  };
  const handleSetCanSubmit = (canSubmit: boolean) => {
    setCanSubmit(canSubmit);
  };

  return (
    <div className="relative py-0 bg-white border-1 border-solid border-gray-200 rounded-xl">
      <div className="flex items-center pt-3 pb-3 px-4">
        <button type="button" aria-label="back" onClick={onGoBack}>
          <MdArrowBack size={26} className="text-gray-500" />
        </button>
        <h1 className="title ml-5">Guest Questions</h1>
      </div>
      <div className="border-b-1 border-gray-200 w-full" />
      <div className="px-4 pt-4">
        <PrePurchaseQuestionsComponent
          ref={prePurchaseQuestionsRef}
          eventId={eventId}
          prePurchaseFormId={prePurchaseFormId}
          handleComplete={onComplete}
          handleSetCanSubmit={handleSetCanSubmit}
        />
        {isLoading && (
          <div className="absolute top-0 left-0 right-0 bottom-0 z-99 bg-white/80">
            <Loader isLoading />
          </div>
        )}
        {canSubmit && (
          <>
            <div className="border-b-1 border-gray-200 mt-3 pt-3 -ml-11 -mr-11" />
            <div className="my-4">
              <Button
                variant="primaryDark"
                onClick={onConfirmPrePurchaseQuestions}
                size="large"
                disabled={isLoading}
                loading={isLoading}
                className="w-full"
              >
                Claim Ticket
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PrePurchaseQuestions;
